'use strict';

import { logWarn, logError } from './bambuserLogger';
import { parseJson } from './bambuserFormatters';

var pendingGetRequestsByUrl = {};
var productDataResponseCacheByUrl = {};
var PRODUCT_DATA_RESPONSE_CACHE_TTL = 5000;

function isProductDataUrl (url) {
    return !!(url && url.indexOf('LiveShopping-ProductData') !== -1);
}

function getCachedProductDataEntry (url) {
    if (!isProductDataUrl(url)) {
        return null;
    }

    var cachedEntry = productDataResponseCacheByUrl[url];
    if (!cachedEntry) {
        return null;
    }

    if ((Date.now() - cachedEntry.timestamp) >= PRODUCT_DATA_RESPONSE_CACHE_TTL) {
        delete productDataResponseCacheByUrl[url];
        return null;
    }

    return cachedEntry;
}

function cacheProductDataSuccess (url, payload) {
    if (!isProductDataUrl(url)) {
        return;
    }

    productDataResponseCacheByUrl[url] = {
        timestamp: Date.now(),
        payload: payload,
    };
}

function cacheProductDataNotFound (url, error) {
    if (!isProductDataUrl(url) || !error || error.status !== 404) {
        return;
    }

    productDataResponseCacheByUrl[url] = {
        timestamp: Date.now(),
        error: error,
    };
}

function executeGetJsonRequest (url, retryBudget) {
    return new Promise(function (resolve, reject) {
        var xhr = new window.XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.setRequestHeader('Accept', 'application/json');
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

        xhr.onload = function () {
            if (xhr.status < 200 || xhr.status >= 300) {
                if (xhr.status === 429 && retryBudget > 0) {
                    logWarn(
                        'GET throttled, retrying',
                        url,
                        'status=' + xhr.status,
                        'retriesLeft=' + retryBudget,
                    );
                    window.setTimeout(function () {
                        executeGetJsonRequest(url, retryBudget - 1)
                            .then(resolve)
                            .catch(reject);
                    }, 300);
                    return;
                }

                var httpError = new Error('HTTP ' + xhr.status);
                httpError.status = xhr.status;
                httpError.url = url;

                if (xhr.status !== 404) {
                    logWarn('GET failed', url, 'status=' + xhr.status);
                }

                reject(httpError);
                return;
            }

            try {
                resolve(JSON.parse(xhr.responseText || '{}'));
            } catch (e) {
                logError(
                    'GET parse error',
                    url,
                    e && e.message ? e.message : e,
                );
                reject(e);
            }
        };

        xhr.onerror = function () {
            logError('GET network error', url);
            reject(new Error('Network error'));
        };

        xhr.send();
    });
}

export function httpGetJson (url, retriesLeft) {
    if (!url) {
        logError('GET aborted: missing url');
        return Promise.reject(new Error('Missing GET url'));
    }

    var cachedProductDataEntry = getCachedProductDataEntry(url);
    if (cachedProductDataEntry) {
        if (cachedProductDataEntry.error) {
            return Promise.reject(cachedProductDataEntry.error);
        }

        return Promise.resolve(cachedProductDataEntry.payload);
    }

    if (pendingGetRequestsByUrl[url]) {
        return pendingGetRequestsByUrl[url];
    }

    var retryBudget = typeof retriesLeft === 'number' ? retriesLeft : 1;
    var requestPromise = executeGetJsonRequest(url, retryBudget).then(
        function (payload) {
            cacheProductDataSuccess(url, payload);
            delete pendingGetRequestsByUrl[url];
            return payload;
        },
        function (error) {
            cacheProductDataNotFound(url, error);
            delete pendingGetRequestsByUrl[url];
            throw error;
        },
    );

    pendingGetRequestsByUrl[url] = requestPromise;
    return requestPromise;
}

export function httpPostForm (url, formData) {
    return new Promise(function (resolve, reject) {
        if (!url) {
            logError('POST aborted: missing url');
            reject(new Error('Missing POST url'));
            return;
        }

        var parts = [];
        var key;
        for (key in formData) {
            if (!Object.prototype.hasOwnProperty.call(formData, key)) {
                continue;
            }

            parts.push(
                encodeURIComponent(key) +
                    '=' +
                    encodeURIComponent(String(formData[key])),
            );
        }

        var xhr = new window.XMLHttpRequest();
        xhr.open('POST', url, true);
        xhr.setRequestHeader(
            'Content-Type',
            'application/x-www-form-urlencoded; charset=UTF-8',
        );
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

        xhr.onload = function () {
            if (xhr.status < 200 || xhr.status >= 300) {
                logWarn('POST failed', url, 'status=' + xhr.status);
                var httpError = new Error('HTTP ' + xhr.status);
                httpError.status = xhr.status;
                httpError.responseText = xhr.responseText;
                reject(httpError);
                return;
            }

            var payload = parseJson(xhr.responseText) || {};
            if (payload.error) {
                logWarn(
                    'POST business error',
                    url,
                    payload.message || 'Add to cart failed',
                );
                var businessError = new Error(
                    payload.message || 'Add to cart failed',
                );
                businessError.type = 'business';
                businessError.payload = payload;
                reject(businessError);
                return;
            }

            resolve(payload);
        };

        xhr.onerror = function () {
            logError('POST network error', url);
            reject(new Error('Network error'));
        };

        xhr.send(parts.join('&'));
    });
}
